/**
 * Accepted review items → clean payload halves for creation (and optional
 * draft apply). Formula groups re-pack POSITIONALLY with their carried
 * weights; rule groups rebuild per-layer trees (an OR root when several);
 * capacity rules re-collect. Rejected items simply vanish — downstream
 * rebalancing normalizes the remaining weights.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowLayer, RuleNode } from '@trafficpulse/shared';

import type {
  FormulaConfigPayload,
} from '@/features/flow-optimizer/clipboard/formula-clipboard.types';

import type { RuleSetPayload } from '../envelope/library-payload.types';
import type { ReviewItem } from './review-items';

export interface AssembledLibraryPayload {
  /** Present when ≥1 formula group was accepted. */
  formula?: FormulaConfigPayload;
  /** Present when ≥1 rule group or capacity rule was accepted. */
  rules?: RuleSetPayload;
}

export function assembleAccepted(
  items: ReviewItem[],
): AssembledLibraryPayload {
  const accepted = items.filter((item) => item.accepted);
  const out: AssembledLibraryPayload = {};
  const formula = assembleFormula(accepted);
  if (formula) out.formula = formula;
  const rules = assembleRules(accepted);
  if (rules) out.rules = rules;
  return out;
}

function assembleFormula(
  accepted: ReviewItem[],
): FormulaConfigPayload | undefined {
  const groups = accepted.filter((item) => item.formulaGroup);
  if (!groups.length) return undefined;
  return {
    groups: groups.map((item) => item.formulaGroup!),
    groupWeights: groups.map((item) => item.weight ?? 100),
  };
}

function assembleRules(accepted: ReviewItem[]): RuleSetPayload | undefined {
  const ruleItems = accepted.filter((item) => item.ruleGroup);
  const capacity = accepted
    .filter((item) => item.capacityRule)
    .map((item) => item.capacityRule!);
  if (!ruleItems.length && !capacity.length) return undefined;
  const tree: Record<FlowLayer, RuleNode | null> = {
    paths: null,
    landers: null,
    offers: null,
  };
  for (const layer of ['paths', 'landers', 'offers'] as const) {
    tree[layer] = layerTree(
      ruleItems.filter((item) => item.layer === layer),
    );
  }
  return { excludeRules: tree, capacityRules: capacity };
}

function layerTree(items: ReviewItem[]): RuleNode | null {
  const groups = items.map((item) => item.ruleGroup!);
  if (groups.length === 0) return null;
  if (groups.length === 1) return groups[0];
  return { kind: 'group', op: 'OR', children: groups };
}
